import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as S from "./styles";
import { FaAngleDown, FaChevronRight, FaHouse } from "react-icons/fa6";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  betHouseOddsNames,
  translatedBets,
  translatedStats,
} from "@/constants/footballStats";

import field from "@/assets/field.svg";
import field90 from "@/assets/field90.svg";
import shirt from "@/assets/Shirt.svg";
import awayShirt from "@/assets/AwayShirt.svg";
import useGameData from "@/hooks/useGameData";
import {
  BetHousesOdds,
  Lineup,
  LineupPlayer,
  LiveComment,
  LiveOdd,
  MatchData,
  MatchDetailsOdds,
  OddsData,
} from "@/interfaces/liveGamesType";
import { api } from "@/services/api";
import AttackMomentum from "@/components/AttackMomentum";
import { StringToNumber } from "@/@types/customTypes";
import WhoWins from "@/components/WhoWins";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import { catchError } from "@/utils/catchError";
import { Link } from "react-router-dom";
import { BiInfoCircle } from "react-icons/bi";
import CarouselTeamsHistoric from "../../../components/CarouselTeamsHistoric/CarouselTeamsHistoric";

// Definição do tipo ResultScore
interface ResultScore {
  homeTeamLogo: string;
  awayTeamLogo: string;
  awayTeamScore: string;
  homeTeamScore: string;
  matchDate: string;
  awayTeamName: string;
  homeTeamName: string;
  draw: boolean;
  awayTeamIsWinner: boolean;
  homeTeamIsWinner: boolean;
}

interface AllMatchData extends MatchData {
  iframeId: number;
  matchOdds: StringToNumber<OddsData>;
  homeLineup?: {
    players: Lineup["starting_lineups"];
    percent: string;
  }[];
  awayLineup?: {
    players: Lineup["starting_lineups"];
    percent: string;
  }[];
  firstTeamResults?: string[];
  secondTeamResults?: string[];
}

interface MatchOdds {
  uniqueOddNames?: { name: string; firstLetter: string }[];
  live_odds?: LiveOdd[];
  liveComments: LiveComment[];
  homeTeamName: string;
  awayTeamName: string;
}

interface FilteredOdds {
  oddName: string;
  filteredOdds: LiveOdd[];
}

const teamPos = ["1.5%", "11%", "21%", "30%", "39%"];

export default function DetailedStatistics() {
  const params = useParams();
  const navigate = useNavigate();

  const { matchId } = params;
  const {
    getMatchIframe,
    getMatchStats,
    getLineUps,
    countResults,
    countResultsScores,
  } = useGameData();

  const [matchData, setMatchData] = useState<AllMatchData>({} as AllMatchData);
  const [matchOdds, setMatchOdds] = useState<MatchOdds>({
    uniqueOddNames: [],
    live_odds: [],
    liveComments: [],
    homeTeamName: "",
    awayTeamName: ""
  });
  const [filteredOdds, setFilteredOdds] = useState<FilteredOdds>({
    oddName: "",
    filteredOdds: [],
  });
  const [betHouses, setBetHouses] = useState<BetHousesOdds[]>([]);

  const [loading, setLoading] = useState(true);
  const [betLoading, setBetLoading] = useState(true);
  const [homeTeamLastScores, setHomeTeamLastScores] = useState<ResultScore[]>([]);
  const [awayTeamLastScores, setAwayTeamLastScores] = useState<ResultScore[]>([]);
  const [headToHeadLoading, setHeadToHeadLoading] = useState(true);
  const matchForm = useForm({
    defaultValues: {
      autocomplete: "",
    },
  });

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const getMatchData = await api.get("/football/getHistMatches", {
          params: {
            matchId,
          },
        });

        let responseData = null;

        if (getMatchData.data?.body?.data) {
          responseData = getMatchData.data.body.data;
        } else if (getMatchData.data?.data) {
          responseData = getMatchData.data.data;
        } else if (getMatchData.data?.body) {
          responseData = getMatchData.data.body;
        } else if (Array.isArray(getMatchData.data)) {
          responseData = getMatchData.data;
        }

        if (!responseData) {
          console.error("No match data found in response:", getMatchData.data);
          toast.error("Ocorreu um erro ao carregar os dados da partida");
          setLoading(false);
          return;
        }

        const dataArray = Array.isArray(responseData) ? responseData : [responseData];

        if (dataArray.length === 0) {
          console.error("Empty match data array");
          toast.error("Não foram encontrados dados para esta partida");
          setLoading(false);
          return;
        }

        const data = dataArray as AllMatchData[];
        console.log("Match data loaded:", data[0]);

        // Obter as estimativas
        const matchStats = await api.get("/football/matchStats", {
          params: {
            matchId: data[0].match_id,
          },
        });

        let matchOdds = null;
        if (matchStats.data?.data) {
          matchOdds = matchStats.data.data;
        } else if (matchStats.data?.body) {
          matchOdds = matchStats.data.body;
        } else {
          matchOdds = matchStats.data;
        }

        console.log("Match odds loaded:", matchOdds);

        let homeLineup: {
          players: LineupPlayer[];
          percent: string;
        }[] | undefined;

        let awayLineup: {
          players: LineupPlayer[];
          percent: string;
        }[] | undefined;

        if (data[0].lineup?.home?.starting_lineups?.length) {
          homeLineup = getLineUps(
            data[0].match_hometeam_system,
            data[0].lineup.home.starting_lineups,
            teamPos
          );
        }
        if (data[0].lineup?.away?.starting_lineups?.length) {
          awayLineup = getLineUps(
            data[0].match_awayteam_system,
            data[0].lineup.away.starting_lineups,
            teamPos
          );
        }

        setMatchData(() => ({
          ...data[0],
          iframeId: -1,
          matchOdds,
          homeLineup,
          awayLineup,
        }));

        await afterInit(data[0]);

        setLoading(false);
      } catch (e) {
        console.error("Error loading match data:", e);
        toast.error("Ocorreu um erro ao carregar os dados");
        setLoading(false);
      }
    };

    const afterInit = async (data: AllMatchData) => {
      try {
        if (!data || !data.match_hometeam_id || !data.match_awayteam_id) {
          console.error("Missing team IDs in match data:", data);
          setHeadToHeadLoading(false);
          return;
        }

        const h2h = await api.get("/football/getH2H", {
          params: {
            firstTeamId: data.match_hometeam_id,
            secondTeamId: data.match_awayteam_id,
          },
        });

        console.log("Full H2H API response:", h2h.data);

        let h2hData;
        if (h2h.data.body?.data) {
          h2hData = h2h.data.body.data;
        } else if (h2h.data.data) {
          h2hData = h2h.data.data;
        } else if (h2h.data.body) {
          h2hData = h2h.data.body;
        } else {
          h2hData = h2h.data;
        }

        console.log("Processed H2H data:", h2hData);

        const firstTeamVsSecondTeam = h2hData.firstTeam_VS_secondTeam || [];
        const firstTeamResults = h2hData.firstTeam_lastResults || [];
        const secondTeamResults = h2hData.secondTeam_lastResults || [];

        console.log("First team results:", firstTeamResults);
        console.log("Second team results:", secondTeamResults);

        const homeTeamResultsScores = countResultsScores(firstTeamResults);
        setHomeTeamLastScores(homeTeamResultsScores || []);

        const awayTeamResultsScores = countResultsScores(secondTeamResults);
        setAwayTeamLastScores(awayTeamResultsScores || []);

        const homeTeamResults = countResults(
          data.match_hometeam_id,
          firstTeamVsSecondTeam
        );

        const awayTeamResults = countResults(
          data.match_awayteam_id,
          firstTeamVsSecondTeam
        );

        setMatchData((prev) => ({
          ...prev,
          firstTeamResults: homeTeamResults,
          secondTeamResults: awayTeamResults,
        }));

        try {
          const iframeId = await getMatchIframe(matchData);

          if (iframeId !== undefined) {
            setMatchData((prev) => ({ ...prev, iframeId }));
          }
        } catch (iframeError) {
          console.error("Error fetching match iframe:", iframeError);
        }

        setHeadToHeadLoading(false);
      } catch (error) {
        console.error("Error processing head-to-head data:", error);
        setHeadToHeadLoading(false);
      }
    };

    const fetchLiveOdds = async () => {
      try {
        const matchId = params.matchId;
        if (!matchId) return;

        const response = await api.get(`/football/getLiveOddsByMatch`, {
          params: {
            matchId,
          },
        });

        let matchData = null;

        if (response.data?.body && response.data.body[matchId]) {
          matchData = response.data.body[matchId];
        } else if (response.data?.data && response.data.data[matchId]) {
          matchData = response.data.data[matchId];
        } else if (response.data?.body) {
          matchData = response.data.body;
        } else if (response.data) {
          matchData = response.data;
        }

        if (matchData) {
          const liveOdds = matchData.live_odds || [];

          const uniqueNames = new Set<string>();
          liveOdds.forEach((odd: LiveOdd) => {
            if (odd.odd_name) {
              uniqueNames.add(odd.odd_name);
            }
          });

          const uniqueOddNames = Array.from(uniqueNames).map(name => ({
            name: translatedBets[name as keyof typeof translatedBets] || name,
            firstLetter: (translatedBets[name as keyof typeof translatedBets] || name)[0]
          }));

          setMatchOdds((prev) => ({
            ...prev,
            live_odds: liveOdds,
            liveComments: matchData.live_comments || [],
            uniqueOddNames: uniqueOddNames,
            homeTeamName: matchData.match_hometeam_name || prev.homeTeamName,
            awayTeamName: matchData.match_awayteam_name || prev.awayTeamName
          }));
        } else {
          console.warn("Live odds data structure not as expected", response.data);
          setMatchOdds((prev) => ({
            ...prev,
            live_odds: [],
            liveComments: []
          }));
        }
      } catch (error) {
        console.error("Error fetching live odds:", error);
        setMatchOdds((prev) => ({
          ...prev,
          live_odds: [],
          liveComments: []
        }));
      }
    };

    const fetchOdds = async () => {
      try {
        const response = await api.get("/football/getOddsByMatch", {
          params: {
            matchId,
          },
        });

        if (response.data && Array.isArray(response.data.data)) {
          setBetHouses(response.data.data);
        } else {
          setBetHouses([]);
          console.warn("Odds data structure not as expected", response.data);
        }
      } catch (error) {
        console.error("Error fetching odds:", error);
        setBetHouses([]);
      }
    };

    const interval = setInterval(() => {
      fetchLiveOdds();
      fetchOdds();
      setBetLoading(false);
    }, 10000);

    init();
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (matchData && matchData.match_id) {
      getImgs();
    }
  }, [matchData?.match_id]);

  const filterOdds = (selectedName?: string) => {
    if (!selectedName) {
      setFilteredOdds({
        filteredOdds: [],
        oddName: "",
      });
      return;
    }

    const translatedBetKeys = Object.keys(translatedBets) as Array<
      keyof typeof translatedBets
    >;

    const englishKey =
      translatedBetKeys.find((key) => translatedBets[key] === selectedName) ||
      selectedName;

    const filtered =
      matchOdds.live_odds?.filter((el) => el.odd_name === englishKey) || [];

    setFilteredOdds({
      filteredOdds: filtered,
      oddName: selectedName,
    });
  };

  const isMedia700 = !useMediaQuery("(min-width: 700px)");

  const half = useMemo(
    () =>
      Math.ceil((matchData.statistics ? matchData.statistics.length : 0) / 2),
    [matchData]
  );

  const firstPartStats = useMemo(
    () => matchData.statistics?.slice(0, half),
    [matchData]
  );

  const secondPartStats = useMemo(
    () => matchData.statistics?.slice(half),
    [matchData]
  );

  const getImgs = async () => {
    try {
      if (!matchData || !matchData.match_hometeam_name || !matchData.match_awayteam_name) {
        console.log("Match data not fully loaded yet for getImgs");
        return;
      }

      const teams = await api.get(`/football/getTeamsByLeague`, {
        params: {
          leagueId: 0,
        },
      });

      const teamsData = teams.data?.data || [];

      const homeTeam = teamsData.find(
        (team: any) => team?.team_name === matchData?.match_hometeam_name
      );
      const awayTeam = teamsData.find(
        (team: any) => team?.team_name === matchData?.match_awayteam_name
      );

      setMatchOdds((prev) => ({
        ...prev,
        homeTeamName: matchData?.match_hometeam_name || "",
        awayTeamName: matchData?.match_awayteam_name || "",
      }));
    } catch (e) {
      console.error("Error fetching team images:", e);
    }
  };

  const totalGames = (matchData.firstTeamResults?.length || 0) + (matchData.secondTeamResults?.length || 0);

  const homeWins = matchData.firstTeamResults?.filter(result => result === 'V').length || 0;
  const awayWins = matchData.secondTeamResults?.filter(result => result === 'V').length || 0;
  const draws = matchData.firstTeamResults?.filter(result => result === 'E').length || 0;

  const homeTeamPercentage = totalGames ? Math.round((homeWins / totalGames) * 100) : 0;
  const awayTeamPercentage = totalGames ? Math.round((awayWins / totalGames) * 100) : 0;
  const drawPercentage = totalGames ? Math.round((draws / totalGames) * 100) : 0;

  const possessionHome = matchData.statistics?.find(stat => stat.type === "Ball Possession")?.home || "0%";
  const possessionAway = matchData.statistics?.find(stat => stat.type === "Ball Possession")?.away || "0%";

  const homeTeamWin = matchData?.matchOdds?.homeTeamWin || "0%";
  const draw = matchData?.matchOdds?.draw || "0%";
  const awayTeamWin = matchData?.matchOdds?.awayTeamWin || "0%";
  const doubleChanceHomeTeamWinOrDraw = matchData?.matchOdds?.doubleChanceHomeTeamWinOrDraw || "0%";
  const doubleChanceAwayTeamWinOrDraw = matchData?.matchOdds?.doubleChanceAwayTeamWinOrDraw || "0%";
  const homeTeamOrAwayTeamToWin = matchData?.matchOdds?.homeTeamOrAwayTeamToWin || "0%";
  const over2point5GoalsPerMatch = matchData?.matchOdds?.over2point5GoalsPerMatch || "0%";
  const under2point5GoalsPerMatch = matchData?.matchOdds?.under2point5GoalsPerMatch || "0%";
  const over1point5GoalsPerMatch = matchData?.matchOdds?.over1point5GoalsPerMatch || "0%";
  const under1point5GoalsPerMatch = matchData?.matchOdds?.under1point5GoalsPerMatch || "0%";
  const over3point5GoalsPerMatch = matchData?.matchOdds?.over3point5GoalsPerMatch || "0%";
  const under3point5GoalsPerMatch = matchData?.matchOdds?.under3point5GoalsPerMatch || "0%";
  const bothTeamToScore = matchData?.matchOdds?.bothTeamToScore || "0%";
  const onlyOneTeamToScore = matchData?.matchOdds?.onlyOneTeamToScore || "0%";

  return (
    <S.GlobalWrapper>
      <S.FakeBreadCrumb>
        <S.BreadCrumbCont>
          <FaHouse />
          <FaChevronRight />
          <p>Dados da Partida</p>
        </S.BreadCrumbCont>
      </S.FakeBreadCrumb>

      <S.TeamWrapper>
        <S.TeamMatch>
          <S.IndividualTeam>
            <S.TeamIcon color="#325176">
              <img src={matchData.team_home_badge} alt="" />
            </S.TeamIcon>
            <p>{matchData.match_hometeam_name}</p>
          </S.IndividualTeam>

          {matchData.team_home_badge && matchData.team_away_badge ? (
            <h3>
              {matchData.match_hometeam_score} :{" "}
              {matchData.match_awayteam_score}
            </h3>
          ) : (
            <CircularProgress />
          )}

          <S.IndividualTeam>
            <p>{matchData.match_awayteam_name}</p>
            <S.TeamIcon color="#993232">
              <img src={matchData.team_away_badge} alt="" />
            </S.TeamIcon>
          </S.IndividualTeam>
        </S.TeamMatch>
      </S.TeamWrapper>

      <Grid container spacing={2}>
        <Grid item lg={12} md={12} xs={12}>
          <S.MetricsWrapper>
            <h2>Métricas</h2>
            <Grid container spacing={2} justifyContent="center">
              {matchData.statistics?.length ? (
                <>
                  <Grid item lg={6} md={12} xs={12}>
                    <S.MetricsContainer>
                      {firstPartStats.map((el) => (
                        <S.GameStatistic
                          homePoints={parseInt(el.home)}
                          awayPoints={parseInt(el.away)}
                          key={el.type}
                        >
                          <span>{el.home}</span>
                          {
                            translatedStats[
                            el.type as keyof typeof translatedStats
                            ]
                          }
                          <span>{el.away}</span>
                        </S.GameStatistic>
                      ))}
                    </S.MetricsContainer>
                  </Grid>
                  <Grid item lg={6} md={12} xs={12}>
                    {secondPartStats.map((el) => (
                      <S.GameStatistic
                        homePoints={parseInt(el.home)}
                        awayPoints={parseInt(el.away)}
                        key={el.type}
                      >
                        <span>{el.home}</span>
                        {
                          translatedStats[
                          el.type as keyof typeof translatedStats
                          ]
                        }
                        <span>{el.away}</span>
                      </S.GameStatistic>
                    ))}
                  </Grid>
                </>
              ) : null}
            </Grid>
          </S.MetricsWrapper>
        </Grid>
        <Grid item lg={6} md={12} xs={12}>
          <S.FieldWrapper>
            <S.FieldCont>
              <img src={isMedia700 ? field90 : field} alt="" />
              {matchData.homeLineup?.map((el) => (
                <S.ShirtsCont position={el.percent} isHome key={el.percent}>
                  {el.players.map((element) => (
                    <Link
                      key={element.player_key}
                      to={`/dashboard/jogador/${matchId}/${element.player_key}`}
                    >
                      <img src={shirt} alt="" />
                      <h2>{element.lineup_number}</h2>
                      <p>{element.lineup_player}</p>
                    </Link>
                  ))}
                </S.ShirtsCont>
              ))}

              {matchData.awayLineup?.map((el) => (
                <S.ShirtsCont position={el.percent} key={el.percent}>
                  {el.players.map((element) => (
                    <Link
                      to={`/dashboard/jogador/${element.player_key}`}
                      key={element.player_key}
                    >
                      <img src={awayShirt} alt="" />
                      <h2>{element.lineup_number}</h2>
                      <p>{element.lineup_player}</p>
                    </Link>
                  ))}
                </S.ShirtsCont>
              ))}
            </S.FieldCont>
            <Grid container spacing={2}>
              <Grid item md={6} sm={6} xs={12}>
                <S.HomeTeamCont>
                  <div>
                    <img src={matchData.team_home_badge} alt="" />
                  </div>
                  <div>
                    {matchData.match_hometeam_name ? (
                      <>
                        <p>{matchData.match_hometeam_system}</p>
                        <h2>{matchData.match_hometeam_name}</h2>
                      </>
                    ) : (
                      <h2>Carregando...</h2>
                    )}
                  </div>
                </S.HomeTeamCont>
              </Grid>
              <Grid item md={6} sm={6} xs={12}>
                <S.AwayTeamCont>
                  <div>
                    {matchData.match_awayteam_name ? (
                      <>
                        <p>{matchData.match_awayteam_system}</p>
                        <h2>{matchData.match_awayteam_name}</h2>
                      </>
                    ) : (
                      <h2>Carregando...</h2>
                    )}
                  </div>
                  <div>
                    <img src={matchData.team_away_badge} alt="" />
                  </div>
                </S.AwayTeamCont>
              </Grid>
            </Grid>
          </S.FieldWrapper>
        </Grid>

        <Grid item lg={6} md={12} xs={12}>
          <S.EstimatesWrapper>
            <h2>Estimativas</h2>
            <div>
              <p>Informação</p>
              <p>Porcentagem</p>
            </div>
            {loading ? (
              <CircularProgress />
            ) : (
              <>
                <div>
                  <p>Dupla Chance - Vitória da Equipe da Casa ou Empate </p>
                  <p>{matchData?.matchOdds?.doubleChanceHomeTeamWinOrDraw}</p>
                </div>
                <div>
                  <p>Dupla Chance - Vitória da Equipe Visitante ou Empate </p>
                  <p>{matchData?.matchOdds?.doubleChanceAwayTeamWinOrDraw}</p>
                </div>
                <div>
                  <p>Vitória da Equipe da Casa ou da Equipe Visitante </p>
                  <p>{matchData?.matchOdds?.homeTeamOrAwayTeamToWin}</p>
                </div>
                <div>
                  <p>Mais de 2,5 Gols no Jogo </p>
                  <p>{matchData?.matchOdds?.over2point5GoalsPerMatch}</p>
                </div>
                <div>
                  <p>Menos de 2,5 Gols no Jogo </p>
                  <p>{matchData?.matchOdds?.under2point5GoalsPerMatch}</p>
                </div>
                <div>
                  <p>Mais de 1,5 Gols no Jogo </p>
                  <p>{matchData?.matchOdds?.over1point5GoalsPerMatch}</p>
                </div>
                <div>
                  <p>Menos de 1,5 Gols no Jogo </p>
                  <p>{matchData?.matchOdds?.under1point5GoalsPerMatch}</p>
                </div>
                <div>
                  <p>Mais de 3,5 Gols no Jogo </p>
                  <p>{matchData?.matchOdds?.over3point5GoalsPerMatch}</p>
                </div>
                <div>
                  <p>Menos de 3,5 Gols no Jogo </p>
                  <p>{matchData?.matchOdds?.under3point5GoalsPerMatch}</p>
                </div>
                <div>
                  <p>Ambas as Equipes Marcam </p>
                  <p>{matchData?.matchOdds?.bothTeamToScore}</p>
                </div>
                <div>
                  <p>Apenas Uma Equipe Marca </p>
                  <p>{matchData?.matchOdds?.onlyOneTeamToScore}</p>
                </div>
              </>
            )}
          </S.EstimatesWrapper>
        </Grid>
        <Grid item lg={12} md={12} xs={12}>
          <S.GraphicsWrapper>
            <h2>Gráficos</h2>
            <S.GraphicsContainer>
              <S.StatsContainer>
                <AttackMomentum matchData={matchData} />
              </S.StatsContainer>

              <p>Histórico</p>
              <S.StatsContainer>
                <S.HistoryTable>
                  <tbody>
                    <S.TableHead>
                      <th>#</th>
                      <th>Time</th>
                      <th>Partidas</th>
                      <th></th>
                    </S.TableHead>

                    <S.TableData>
                      <td>1</td>
                      <td>
                        <img src={matchData.team_home_badge} alt="" />
                      </td>
                      <td>
                        <S.MatchesTable>
                          {matchData.firstTeamResults?.map((el, i) => (
                            <S.H2hSpan key={i} stats={el}>
                              {el}
                            </S.H2hSpan>
                          ))}
                        </S.MatchesTable>
                      </td>
                    </S.TableData>

                    <S.TableData>
                      <td>2</td>
                      <td>
                        <img src={matchData.team_away_badge} alt="" />
                      </td>
                      <td>
                        <S.MatchesTable>
                          {matchData.secondTeamResults?.map((el, i) => (
                            <S.H2hSpan key={i} stats={el}>
                              {el}
                            </S.H2hSpan>
                          ))}
                        </S.MatchesTable>
                      </td>
                    </S.TableData>
                  </tbody>
                </S.HistoryTable>
              </S.StatsContainer>
              {loading ? (
                <CircularProgress />
              ) : (
                <WhoWins
                  homeTeamPercentage={homeTeamPercentage}
                  awayTeamPercentage={awayTeamPercentage}
                  draw={drawPercentage}
                />
              )}
            </S.GraphicsContainer>
          </S.GraphicsWrapper>
        </Grid>

        <Grid item lg={12} md={12} xs={12}>
          <S.scoresH2hContainer>
            <h2>Histórico de placar</h2>
            {headToHeadLoading ? (
              <CircularProgress />
            ) : (
              <div>
                <h2>{matchData?.match_hometeam_name}</h2>
                <CarouselTeamsHistoric
                  scores={homeTeamLastScores?.length ? homeTeamLastScores : []}
                />
                <h2>{matchData.match_awayteam_name || matchOdds.awayTeamName}</h2>
                <CarouselTeamsHistoric
                  scores={awayTeamLastScores?.length ? awayTeamLastScores : []}
                />
              </div>
            )}
          </S.scoresH2hContainer>
        </Grid>
      </Grid>

      <Grid container pt={2}>
        <Grid item xs={12}>
          <S.BetsOnMatch>
            <S.TitleSection>
              <h2>Bets na Partida</h2>{" "}
              <Tooltip
                title="Média calculada de todas as odds do jogo em tempo real."
                placement="top-end"
              >
                <button>
                  <BiInfoCircle />
                </button>
              </Tooltip>
            </S.TitleSection>

            {!betLoading ? (
              matchOdds.live_odds && matchOdds.live_odds.length > 0 ? (
                <>
                  <Autocomplete
                    renderInput={(params) => (
                      <TextField {...params} label="Selecione uma bet" />
                    )}
                    options={matchOdds.uniqueOddNames ?
                      matchOdds.uniqueOddNames.sort(
                        (a, b) => -b.firstLetter.localeCompare(a.firstLetter)
                      ) : []
                    }
                    groupBy={(option) => option.firstLetter}
                    getOptionLabel={(option) => option.name}
                    onChange={(e, data) => {
                      filterOdds(data?.name);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.name === value.name
                    }
                  />
                  {filteredOdds.oddName ? (
                    <Accordion>
                      <AccordionSummary expandIcon={<FaAngleDown />}>
                        <h3>{filteredOdds.oddName}</h3>
                      </AccordionSummary>
                      <AccordionDetails>
                        <S.BetNames>
                          <p>
                            Última Atualização:{" "}
                            {filteredOdds.filteredOdds[0].upd.split(" ")[1]}
                          </p>
                        </S.BetNames>
                        <S.BetCardWrapper>
                          <Grid container spacing={2}>
                            {filteredOdds.filteredOdds.map((el) => (
                              <Grid item md={4} sm={6} xs={12}>
                                <S.BetCardCont>
                                  <S.CardPillCont>
                                    <S.BetCardPill>
                                      <span>
                                        <div>Tipo:</div>
                                        {el.type}
                                      </span>{" "}
                                      <span>
                                        <div>Odd:</div>
                                        {el.value}
                                      </span>
                                    </S.BetCardPill>
                                    {el.handicap ? (
                                      <S.BetCardPill>
                                        handicap: <span>{el.handicap}</span>
                                      </S.BetCardPill>
                                    ) : (
                                      <></>
                                    )}
                                  </S.CardPillCont>
                                </S.BetCardCont>
                              </Grid>
                            ))}
                          </Grid>
                        </S.BetCardWrapper>
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <S.NotFound>
                      <p>Selecione uma bet para analisa-la</p>
                    </S.NotFound>
                  )}
                </>
              ) : (
                <S.NotFound>
                  <p>Nenhuma bet encontrada</p>
                </S.NotFound>
              )
            ) : (
              <CircularProgress />
            )}
          </S.BetsOnMatch>
        </Grid>
      </Grid>

      <Grid container pt={2}>
        <Grid item xs={12}>
          <S.BetsOnMatch>
            <Accordion>
              <AccordionSummary expandIcon={<FaAngleDown />}>
                <h3>Bets das casas de apostas</h3>
              </AccordionSummary>
              <AccordionDetails
                sx={(props) => ({
                  backgroundColor: props.palette.backgroundColor.main,
                })}
              >
                {betLoading ? (
                  <Grid container justifyContent="center">
                    <CircularProgress />
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    {betHouses?.map((el, i) => (
                      <Grid item md={4} width="100%" key={el.odd_bookmakers}>
                        <Accordion>
                          <AccordionSummary expandIcon={<FaAngleDown />}>
                            <h3>{el.odd_bookmakers}</h3>{" "}
                          </AccordionSummary>
                          <AccordionDetails>
                            <S.BetNames>
                              <p>
                                Última Atualização: {el.odd_date.split(" ")[1]}
                              </p>
                            </S.BetNames>
                            <S.BetCardWrapper>
                              {(
                                Object.keys(betHouseOddsNames) as Array<
                                  keyof typeof betHouseOddsNames
                                >
                              ).map((key) =>
                                el[key] ? (
                                  <S.BetCardCont key={key}>
                                    <S.CardPillCont>
                                      <S.BetCardPill>
                                        {betHouseOddsNames[key]}:{" "}
                                        <span>{el[key]}</span>
                                      </S.BetCardPill>
                                    </S.CardPillCont>
                                  </S.BetCardCont>
                                ) : (
                                  <></>
                                )
                              )}
                            </S.BetCardWrapper>
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          </S.BetsOnMatch>
        </Grid>
      </Grid>

      <Grid container pt={2}>
        <Grid item xs={12}>
          <S.LineUpsWrapper>
            <h2>Escalações</h2>
            {/* <S.SmallLineUp>Clique no jogador para saber mais </S.SmallLineUp> */}
            <Grid container spacing={3} pt={2}>
              <Grid item lg={6} md={12} xs={12}>
                <S.PlayersCont
                  color="#2af497"
                  to={`/dashboard/jogador/${matchId}/${matchData.lineup?.home.coach[0].player_key}`}
                >
                  <div></div>
                  <div>
                    <p>
                      {matchData.lineup?.home?.coach && matchData.lineup.home.coach.length > 0
                        ? matchData.lineup.home.coach[0]?.lineup_player
                        : "Não encontrado"}
                    </p>
                    <small>Técnico</small>
                  </div>
                </S.PlayersCont>
                <Divider
                  sx={(props) => ({
                    my: 2,
                    borderWidth: "2px",
                    borderColor: props.palette.bottomColor.main,
                  })}
                />
                {matchData.lineup?.home?.starting_lineups?.map((el) => (
                  <S.PlayersCont
                    color="#2af497"
                    key={`home-${el.player_key || el.lineup_number}-${el.lineup_player}`}
                    to={`/dashboard/jogador/${matchId}/${matchData.lineup?.away.coach[0].player_key}`}
                  >
                    <div></div>
                    <div>
                      <p>{el.lineup_player}</p>
                    </div>
                    <h3>{el.lineup_number}</h3>
                  </S.PlayersCont>
                )) || null}
              </Grid>
              <Grid item lg={6} md={12} xs={12}>
                <S.PlayersCont
                  color="#325176"
                  to="#"
                >
                  <div></div>
                  <div>
                    <p>
                      {matchData.lineup?.away?.coach && matchData.lineup.away.coach.length > 0
                        ? matchData.lineup.away.coach[0]?.lineup_player
                        : "Não encontrado"}
                    </p>
                    <small>Técnico</small>
                  </div>
                </S.PlayersCont>
                <Divider
                  sx={(props) => ({
                    my: 2,
                    borderWidth: "2px",
                    borderColor: props.palette.bottomColor.main,
                  })}
                />
                {matchData.lineup?.away?.starting_lineups?.map((el) => (
                  <S.PlayersCont
                    color="#325176"
                    key={`away-${el.player_key || el.lineup_number}-${el.lineup_player}`}
                    to="#"
                  >
                    <div></div>
                    <div>
                      <p>{el.lineup_player}</p>
                    </div>
                    <h3>{el.lineup_number}</h3>
                  </S.PlayersCont>
                )) || null}
              </Grid>
            </Grid>
          </S.LineUpsWrapper>
        </Grid>
      </Grid>
    </S.GlobalWrapper>
  );
}
